// /CalculationsScreen/components/CleanCalculationTable/index.tsx
import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useCleanCalculation } from './useCleanCalculationTable';
import RoomHeader from '../../utils/RoomHeader';
import classes from './cleanCalculationTable.module.scss';
import { useStore } from 'hooks/useStore';
import { CleanCalculationsResponse } from 'Interfaces/CalculationInterfaces/MainCalculationInterfaces';

interface CleanCalculationScreenProps {
  roomData: CleanCalculationsResponse;
  loading: boolean;
  expandedRows: any;
  setExpandedRows: (rows: any) => void;
}

// Helper function to render time values with error handling
const renderTimeValue = (value: any, formatCleanTime: (time: any) => string) => {
  const formattedValue = formatCleanTime(value);
  return formattedValue === 'Error' ? <span style={{ color: 'red' }}>Error</span> : formattedValue;
};

// Helper function to render simple field values
const renderFieldValue = (value: any) => value || '-';

// Column configuration for better maintainability
const getColumnConfig = (formatCleanTime: (time: any) => string) => [
  {
    field: 'cleanId',
    header: 'Clean ID',
    body: (rowData: any) => renderFieldValue(rowData.cleanId)
  },
  {
    field: 'outgoingJobId',
    header: 'Outgoing Job',
    body: (rowData: any) => renderFieldValue(rowData.outgoingJobId)
  },
  {
    field: 'incomingJobId',
    header: 'Incoming Job',
    body: (rowData: any) => renderFieldValue(rowData.incomingJobId)
  },
  {
    field: 'cleanType',
    header: 'Clean Type',
    body: (rowData: any) => renderFieldValue(rowData.cleanType)
  },
  {
    field: 'cleanOverride',
    header: 'Clean Override',
    body: (rowData: any) => renderFieldValue(rowData.cleanOverride)
  },
  {
    field: 'cleanTime',
    header: 'Clean Time',
    body: (rowData: any) => renderTimeValue(rowData.cleanTime, formatCleanTime)
  },
  {
    field: 'clearTime',
    header: 'Clear Time',
    body: (rowData: any) => renderTimeValue(rowData.clearTime, formatCleanTime)
  },
  {
    field: 'setupTime',
    header: 'Setup Time',
    body: (rowData: any) => renderTimeValue(rowData.setupTime, formatCleanTime)
  },
  {
    field: 'lvTime',
    header: 'LV Time',
    body: (rowData: any) => renderTimeValue(rowData.lvTime, formatCleanTime)
  },
  {
    field: 'changeoverR2',
    header: 'Changeover R2',
    body: (rowData: any) => renderTimeValue(rowData.changeoverR2, formatCleanTime)
  },
  {
    field: 'color',
    header: 'Color',
    body: (rowData: any) => renderFieldValue(rowData.color)
  }
];

// Extracted component for rendering a single room
const RoomCalculationTable: React.FC<{
  room: any;
  isExpanded: boolean;
  onToggle: () => void;
  formatCleanTime: (time: any) => string;
}> = ({ room, isExpanded, onToggle, formatCleanTime }) => {
  const columnConfig = getColumnConfig(formatCleanTime);

  return (
    <div className={classes.roomSection}>
      <RoomHeader
        name={room.name}
        roomId={room.id}
        isExpanded={isExpanded}
        onToggle={onToggle}
      />
      {isExpanded && (
        <DataTable
          value={room.calculations}
          emptyMessage="No records found"
          className={classes.dataTable}
          rowClassName={() => classes.tableRow}
          resizableColumns
          columnResizeMode="expand"
          scrollable
          scrollHeight="flex"
        >
          {columnConfig.map((column, index) => (
            <Column
              key={`${column.field}-${column.header}`}
              field={column.field}
              header={column.header}
              className={classes.column}
              body={column.body}
            />
          ))}
        </DataTable>
      )}
    </div>
  );
};

const CleanCalculationScreen: React.FC<CleanCalculationScreenProps> = ({
  roomData,
  loading,
  expandedRows,
  setExpandedRows
}) => {
  const { formatCleanTime } = useCleanCalculation();
  const [expandedRooms, setExpandedRooms] = useState<Record<number, boolean>>({});
  const { roomStore } = useStore();

  // Add a state to track if we've received a response
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);

  // Reset the response flag when loading changes to true
  useEffect(() => {
    if (loading === true) {
      setHasReceivedResponse(false);
    }
  }, [loading]);

  // Set the response flag when we receive data (when loading becomes false)
  useEffect(() => {
    if (loading === false) {
      setHasReceivedResponse(true);
    }
  }, [loading]);

  // Ensure room data is loaded
  useEffect(() => {
    if (roomStore.rooms.length === 0) {
      roomStore.fetchRooms(1, 100);
    }
  }, []);

  // Expand all accordions whenever roomData changes
  useEffect(() => {
    if (roomData && roomData.length > 0) {
      // Create object with all rooms expanded
      const expandAll: Record<number, boolean> = {};
      roomData.forEach(room => {
        expandAll[room.id] = true;
      });
      setExpandedRooms(expandAll);
    }
  }, [roomData]);

  // Check if roomData has calculations property
  const hasValidData = () => {
    return (
      roomData &&
      roomData.length > 0 &&
      roomData.some(room => room.calculations && room.calculations.length > 0)
    );
  };

  // Toggle room expansion
  const toggleRoom = (roomId: number) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // Show loading spinner when data is being fetched or when we haven't received a response
  if (loading === true || !hasReceivedResponse) {
    return (
      <div className={classes.loadingContainer}>
        <ProgressSpinner className={classes.spinner} />
        <div>Loading calculation data...</div>
      </div>
    );
  }

  // Only show "No data found" when we've received a response but it's empty
  if (hasReceivedResponse && !hasValidData()) {
    return (
      <div className={classes.emptyMessage}>
        No calculation data found. Please check your filters or try again later.
      </div>
    );
  }

  return (
    <div className={classes.cleanCalculationContainer}>
      {roomData.map(room => (
        <RoomCalculationTable
          key={room.id}
          room={room}
          isExpanded={!!expandedRooms[room.id]}
          onToggle={() => toggleRoom(room.id)}
          formatCleanTime={formatCleanTime}
        />
      ))}
    </div>
  );
};

export default CleanCalculationScreen;
